import axios from "axios";

export type ExternalReportModule = "orderTrackingCenter" | "openOrderReport";

export interface ExternalReportSettings {
  accessToken: string;
  orderTrackingCenterUrl: string;
  openOrderReportUrl: string;
}

export interface ExternalReportRow {
  model: string;
  date: string;
  salePrice: number | null;
  cost: number | null;
  gm: number | null;
}

const STORAGE_KEY = "external-report-settings";

const DEFAULT_SETTINGS: ExternalReportSettings = {
  accessToken: "",
  orderTrackingCenterUrl: "",
  openOrderReportUrl: "",
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = String(value).replace(/[^0-9.-]/g, "");
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const getArrayPayload = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

export const getExternalReportSettings = (): ExternalReportSettings => {
  if (typeof window === "undefined") {
    return DEFAULT_SETTINGS;
  }

  try {
    const rawValue = localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return DEFAULT_SETTINGS;
    }

    const parsed = JSON.parse(rawValue);
    return {
      accessToken: toText(parsed?.accessToken),
      orderTrackingCenterUrl: toText(parsed?.orderTrackingCenterUrl),
      openOrderReportUrl: toText(parsed?.openOrderReportUrl),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

export const saveExternalReportSettings = (
  settings: ExternalReportSettings,
): ExternalReportSettings => {
  const sanitizedSettings: ExternalReportSettings = {
    accessToken: toText(settings.accessToken),
    orderTrackingCenterUrl: toText(settings.orderTrackingCenterUrl),
    openOrderReportUrl: toText(settings.openOrderReportUrl),
  };

  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizedSettings));
  }

  return sanitizedSettings;
};


export const validateExternalReportToken = (
  token?: string,
): { isValid: boolean; message: string } => {
  const resolvedToken = toText(token ?? getExternalReportSettings().accessToken);

  if (!resolvedToken) {
    return {
      isValid: false,
      message: "Add an external report token in Settings before calling these APIs.",
    };
  }

  return {
    isValid: true,
    message: "Token is available and will be used when these APIs are called.",
  };
};

export const normalizeExternalReportRows = (
  payload: unknown,
): ExternalReportRow[] => {
  return getArrayPayload(payload).map((item: Record<string, unknown>) => ({
    model: toText(item?.model ?? item?.Model ?? item?.eqModel),
    date: toText(
      item?.date ??
        item?.Date ??
        item?.poDate ??
        item?.invoiceDate ??
        item?.createdDate,
    ),
    salePrice: toNumberOrNull(
      item?.salePrice ??
        item?.sale_price ??
        item?.salesPrice ??
        item?.sale ??
        item?.price,
    ),
    cost: toNumberOrNull(item?.cost ?? item?.Cost),
    gm: toNumberOrNull(
      item?.gm ?? item?.GM ?? item?.grossMargin ?? item?.gross_margin,
    ),
  }));
};

const getModuleUrl = (
  moduleName: ExternalReportModule,
  settings: ExternalReportSettings,
) => {
  if (moduleName === "orderTrackingCenter") {
    return settings.orderTrackingCenterUrl;
  }

  return settings.openOrderReportUrl;
};

export const fetchExternalReport = async (
  moduleName: ExternalReportModule,
  params?: Record<string, any>,
): Promise<ExternalReportRow[]> => {
  const settings = getExternalReportSettings();
  const tokenValidation = validateExternalReportToken(settings.accessToken);

  if (!tokenValidation.isValid) {
    throw new Error(tokenValidation.message);
  }

  const endpoint = getModuleUrl(moduleName, settings);
  if (!endpoint) {
    throw new Error(
      `No ${moduleName === "orderTrackingCenter" ? "Order Tracking Center" : "Open Order Report"} endpoint has been added in Settings yet.`,
    );
  }

  const response = await axios.get(endpoint, {
    params,
    headers: {
      Authorization: `Bearer ${settings.accessToken}`,
      "X-Access-Token": settings.accessToken,
    },
  });

  return normalizeExternalReportRows(response.data);
};

export const fetchOrderTrackingCenterReport = (
  params?: Record<string, any>,
) => fetchExternalReport("orderTrackingCenter", params);

export const fetchOpenOrderReport = (params?: Record<string, any>) =>
  fetchExternalReport("openOrderReport", params);
